/**
 * Route-level error element.
 *
 * Its most important job is recovering from a FAILED LAZY CHUNK IMPORT. With
 * route-level code splitting, a page's JS is fetched on navigation, so it can
 * fail for reasons that have nothing to do with the code:
 *  - a new deploy replaced the hashed chunk the open tab still points to,
 *  - the network dropped between two navigations,
 *  - an offline Tauri window.
 *
 * Without this, such a failure throws during render and leaves a permanently
 * blank screen. Here we show a readable recovery UI instead. For a stale-deploy
 * failure the correct fix is a hard reload (it re-fetches index.html and the
 * new chunk manifest), which is exactly what the primary action does.
 */

import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/Button";
import { isChunkLoadError } from "@/lib/chunk-error";

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const chunkFailure = isChunkLoadError(error);
  const status = isRouteErrorResponse(error) ? error.status : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[420px] rounded-lg border border-border bg-card p-6 text-center shadow-card">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg border border-danger/25 bg-danger-dim text-danger">
          <TriangleAlert className="h-5 w-5" />
        </div>
        <h1 className="text-sm font-semibold text-text mb-1">
          {chunkFailure ? t("states.updateAvailableTitle") : t("states.error")}
        </h1>
        <p className="text-xs text-text2">
          {chunkFailure ? t("states.updateAvailableMessage") : t("states.errorLoadMessage")}
          {status ? ` (${status})` : ""}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> {t("states.reload")}
          </Button>
          {!chunkFailure && (
            <Button variant="ghost" onClick={() => navigate("/")}>
              {t("states.backHome")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
